<script setup lang="ts">
import { type PropType } from "vue";
import type { CompositionSpacerSize, UIColor } from "../../..";

defineProps({
  /*
      The direction of the spacer.
      values: horizontal, vertical
    */
  direction: {
    type: String as PropType<"horizontal" | "vertical">,
    required: true,
  },

  /*
      The size of the spacer
      values: collapsed, small, medium, large
    */
  size: {
    type: String as PropType<CompositionSpacerSize>,
    required: true,
  },

  /*
      Adds a visual border
    */
  color: {
    type: String as PropType<UIColor>,
    required: false,
  },
});
</script>

<template>
  <div
    :class="[
      $style.container,
      $style[direction],
      $style[size],
      color && $style.color,
    ]"
    :style="{ '--composition-spacer-color': `var(${color})` }"
  >
    <slot name="container" />
  </div>
</template>

<style module>
.container {
  display: flex;
  position: relative;
  flex-shrink: 0;
  width: 0;
  height: 0;
}

.container.color:after {
  content: "";
  display: block;
  position: absolute;
  height: 1px;
  top: 50%;
  width: 100%;
  background-color: var(--composition-spacer-color);
}

.visible.vertical:after {
  height: 1px;
  top: 50%;
  width: 100%;
}

.visible.horizontal:after {
  width: 1px;
  left: 50%;
  height: 100%;
}

.horizontal.collapsed {
  width: 2px;
  height: 100%;
}

.horizontal.blip {
  width: calc(1rem / 4);
  height: 100%;
}

.horizontal.tiny {
  width: calc(2rem / 4);
  height: 100%;
}

.horizontal.small {
  width: 1rem;
  height: 100%;
}

.horizontal.medium {
  width: 2rem;
  height: 100%;
}

.horizontal.large {
  width: 5rem;
  height: 100%;
}

.vertical.collapsed {
  width: 100%;
  height: 2px;
}

.vertical.blip {
  width: 100%;
  height: calc(1rem / 4);
}

.vertical.tiny {
  width: 100%;
  height: calc(2rem / 4);
}

.vertical.small {
  width: 100%;
  height: 1rem;
}

.vertical.medium {
  width: 100%;
  height: 2rem;
}

.vertical.large {
  width: 100%;
  height: 5rem;
}
</style>
